import { z } from 'zod';
import { SPORT_IDS } from '../sports/index';

const sportIdEnum = z.enum(SPORT_IDS as [string, ...string[]]);

export const PLAYER_INTENT_TYPES = [
  'LOOKING_FOR_GAME',
  'ORGANIZING_GAME',
  'AVAILABLE_TO_JOIN',
  'LOOKING_FOR_REGULAR_GROUP',
] as const;

export const PLAYER_INTENT_STATUSES = [
  'active',
  'matched',
  'expired',
  'cancelled',
] as const;

// Самооценка-бакеты (ordinal beginner < amateur < confident < pro) — из users.skill_levels.
export const SKILL_BUCKETS = ['beginner', 'amateur', 'confident', 'pro'] as const;

export const playerIntentTypeSchema = z.enum(PLAYER_INTENT_TYPES);
export const playerIntentStatusSchema = z.enum(PLAYER_INTENT_STATUSES);
export const skillBucketSchema = z.enum(SKILL_BUCKETS);

const playerIntentBaseSchema = z.object({
  sport: sportIdEnum,
  type: playerIntentTypeSchema,
  title: z.string().max(200).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  skillMin: skillBucketSchema.nullable().optional(),
  skillMax: skillBucketSchema.nullable().optional(),
  // ISO-строки от клиента → Date (nullable проверяется до coerce, null не портится).
  availableFrom: z.coerce.date().nullable().optional(),
  availableTo: z.coerce.date().nullable().optional(),
  district: z.string().max(200).nullable().optional(),
  targetCount: z.number().int().min(1).max(100).nullable().optional(),
});

// Кросс-полевые проверки: скилл-вилка по порядку бакетов, окно доступности по времени.
// Срабатывают только когда обе границы заданы (для partial-патча половинку не режем).
type IntentRangeFields = {
  skillMin?: SkillBucket | null;
  skillMax?: SkillBucket | null;
  availableFrom?: Date | null;
  availableTo?: Date | null;
};
const skillRangeOk = (d: IntentRangeFields) =>
  !d.skillMin || !d.skillMax || SKILL_BUCKETS.indexOf(d.skillMin) <= SKILL_BUCKETS.indexOf(d.skillMax);
const availRangeOk = (d: IntentRangeFields) =>
  !d.availableFrom || !d.availableTo || d.availableFrom.getTime() <= d.availableTo.getTime();
const withRangeChecks = <T extends z.ZodTypeAny>(schema: T) =>
  schema
    .refine(skillRangeOk, { message: 'skillMin must be ≤ skillMax', path: ['skillMax'] })
    .refine(availRangeOk, { message: 'availableFrom must be ≤ availableTo', path: ['availableTo'] });

export const createPlayerIntentSchema = withRangeChecks(playerIntentBaseSchema);

// Правка автора: спорт/тип/статус неизменны (статус меняется через cancel/convert).
export const updatePlayerIntentSchema = withRangeChecks(
  playerIntentBaseSchema.omit({ sport: true, type: true }).partial(),
);

export const createIntentCommentSchema = z.object({
  text: z.string().trim().min(1).max(2000),
});

// Публичная лента отдаёт ТОЛЬКО active (см. сервис) — status как фильтр не
// принимаем, чтобы cancelled/expired нельзя было перечислить через API.
export const listPlayerIntentsQuerySchema = z.object({
  cityId: z.string().min(1).max(100).optional(),
  sport: sportIdEnum.optional(),
  type: playerIntentTypeSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

// Опц. gameId — если вызывающий уже создал игру, шов проставляет linked_game_id + matched.
export const convertIntentToGameSchema = z.object({
  gameId: z.string().uuid().nullable().optional(),
});

export type PlayerIntentType = z.infer<typeof playerIntentTypeSchema>;
export type PlayerIntentStatus = z.infer<typeof playerIntentStatusSchema>;
export type SkillBucket = z.infer<typeof skillBucketSchema>;
export type CreatePlayerIntentInput = z.infer<typeof createPlayerIntentSchema>;
export type UpdatePlayerIntentInput = z.infer<typeof updatePlayerIntentSchema>;
export type ListPlayerIntentsQuery = z.infer<typeof listPlayerIntentsQuerySchema>;
